import type { LodgingFolioLineInput, LodgingFolioSettleInput, LodgingSettlementVoidInput } from "@clubcore/api";
import { useMutation, useQueries, useQuery } from "@tanstack/react-query";
import { useScope } from "./scope";

/** How often the exceptions badge and the night audit card re-read the server. */
export const EXCEPTIONS_REFRESH_MS = 5 * 60_000;

function useRefreshLodging() {
  const { queryClient, slug } = useScope();
  return () => {
    void queryClient.invalidateQueries({ queryKey: [slug, "lodging"] });
  };
}

// --- check-in and check-out --------------------------------------------------------------------------------

export function useFrontDeskMutations(stayId: string) {
  const { api } = useScope();
  const refresh = useRefreshLodging();
  return {
    checkIn: useMutation({ mutationFn: () => api.lodging.checkIn(stayId), onSuccess: refresh }),
    checkOut: useMutation({ mutationFn: () => api.lodging.checkOut(stayId), onSuccess: refresh }),
  };
}

// --- folios ----------------------------------------------------------------------------------------------

/** The stay's windows (one, or two when the extras go to another account). */
export function useStayFolios(stayId: string | undefined, enabled = true) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "lodging", "stay-folios", stayId],
    enabled: enabled && Boolean(stayId),
    queryFn: ({ signal }) => api.lodging.stayFolios(stayId!, signal),
  });
}

export function useFolio(folioId: string | undefined) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "lodging", "folio", folioId],
    enabled: Boolean(folioId),
    queryFn: ({ signal }) => api.lodging.getFolio(folioId!, signal),
  });
}

/** Every window's detail at once (for the check-out preview): one request per window, at most two. */
export function useFolioDetails(folioIds: readonly string[]) {
  const { api, slug } = useScope();
  return useQueries({
    queries: folioIds.map((id) => ({
      queryKey: [slug, "lodging", "folio", id],
      staleTime: 0,
      queryFn: ({ signal }: { signal: AbortSignal }) => api.lodging.getFolio(id, signal),
    })),
  });
}

export function useFolioMutations(stayId: string, folioId: string | undefined) {
  const { api } = useScope();
  const refresh = useRefreshLodging();
  const id = () => folioId ?? "";
  return {
    addLine: useMutation({ mutationFn: (input: LodgingFolioLineInput) => api.lodging.addFolioLine(id(), input), onSuccess: refresh }),
    reverseLine: useMutation({ mutationFn: (v: { lineId: string; reason: string }) => api.lodging.reverseFolioLine(id(), v.lineId, { reason: v.reason }), onSuccess: refresh }),
    settle: useMutation({ mutationFn: (input: LodgingFolioSettleInput) => api.lodging.settleFolio(id(), input), onSettled: refresh }),
    voidSettlement: useMutation({
      mutationFn: (v: { settlementId: string; input: LodgingSettlementVoidInput }) => api.lodging.voidSettlement(id(), v.settlementId, v.input),
      onSuccess: refresh,
    }),
    changePayer: useMutation({ mutationFn: (memberId: string) => api.lodging.changeFolioPayer(id(), { payer_member_id: memberId }), onSuccess: refresh }),
    openWindow: useMutation({ mutationFn: (memberId: string) => api.lodging.openFolioWindow(stayId, { payer_member_id: memberId }), onSuccess: refresh }),
  };
}

/** Billing's departments, for a folio line that is not a charge item. */
export function useBillingDepartments(enabled: boolean) {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "lodging", "billing-departments"],
    enabled,
    staleTime: 5 * 60_000,
    retry: false,
    queryFn: ({ signal }) => api.billing.listDepartments(signal),
  });
}

// --- exceptions and the night audit -------------------------------------------------------------------------

export function useExceptions() {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "lodging", "exceptions"],
    refetchInterval: EXCEPTIONS_REFRESH_MS,
    refetchIntervalInBackground: false,
    queryFn: ({ signal }) => api.lodging.exceptions(signal),
  });
}

export function useNightAudit() {
  const { api, slug } = useScope();
  return useQuery({
    queryKey: [slug, "lodging", "night-audit"],
    refetchInterval: EXCEPTIONS_REFRESH_MS,
    refetchIntervalInBackground: false,
    queryFn: ({ signal }) => api.lodging.nightAudit(signal),
  });
}
